import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { Check, X, ShieldCheck, Award, Receipt, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState({ receipts: [], certificates: [] });
  const [tab, setTab] = useState("receipts");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => {
    const params = {};
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    api.get("/approvals/pending", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { load(); }, [user]);

  const approve = async (type, id) => {
    try {
      await api.post(`/${type}/${id}/approve`, { reason: "" });
      toast.success("Approved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    try {
      await api.post(`/${rejecting.kind}/${rejecting.id}/reject`, { reason });
      toast.success("Rejected");
      setRejecting(null); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">{lang === "hi" ? "अनुमोदन" : "Approvals"}</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-[#0A3161]" />
              {lang === "hi" ? "मेकर-चेकर अनुमोदन" : "Maker-Checker Queue"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {lang === "hi" ? "लंबित भुगतान एवं प्रमाणपत्र — अनुमोदन हेतु।" : "Pending payments & certificates awaiting your approval."}
            </p>
          </div>
        </div>

        <div className="flex border-b border-slate-200 mb-4">
          {[
            { k: "receipts", lbl: lang === "hi" ? "कर भुगतान" : "Tax Payments", icon: Receipt, count: data.receipts.length },
            { k: "certificates", lbl: lang === "hi" ? "प्रमाणपत्र" : "Certificates", icon: Award, count: data.certificates.length },
          ].map((x) => {
            const I = x.icon;
            return (
              <button key={x.k} onClick={() => setTab(x.k)} data-testid={`appr-tab-${x.k}`}
                className={`px-5 py-3 text-sm font-semibold border-b-2 inline-flex items-center gap-2 transition-colors ${
                  tab === x.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"
                }`}>
                <I className="w-4 h-4" /> {x.lbl}
                {x.count > 0 && (
                  <span className="px-2 py-0.5 text-[10px] rounded-full bg-[#E87722] text-white font-bold">{x.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {tab === "receipts" ? (
          <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="appr-receipts-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">{t("receiptNo")}</th>
                  <th className="text-left px-4 py-3">{t("propertyNo")}</th>
                  <th className="text-left px-4 py-3">{t("owner")}</th>
                  <th className="text-right px-4 py-3">{t("amount") || "Amount"}</th>
                  <th className="text-left px-4 py-3">{t("paymentMode")}</th>
                  <th className="text-right px-4 py-3">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data.receipts.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs">{r.receipt_no}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.property_no}</td>
                    <td className="px-4 py-3 font-medium">{r.owner_name}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatINR(r.amount_paid)}</td>
                    <td className="px-4 py-3 uppercase text-xs">{r.payment_mode}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button data-testid={`appr-recpt-approve-${r.id}`} onClick={() => approve("tax/receipts", r.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-white" style={{ background: "#107C10" }}>
                        <Check className="w-3 h-3" /> Approve
                      </button>
                      <button data-testid={`appr-recpt-reject-${r.id}`} onClick={() => setRejecting({ kind: "tax/receipts", id: r.id, label: r.receipt_no })}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md border border-red-200 text-red-700">
                        <X className="w-3 h-3" /> Reject
                      </button>
                    </td>
                  </tr>
                ))}
                {data.receipts.length === 0 && (
                  <tr><td colSpan={6} className="py-12 text-center text-slate-400 text-sm">No pending payments</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="appr-certs-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-3">{t("certificateNo")}</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-right px-4 py-3">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data.certificates.map((c) => {
                  const name = c.data?.child_name || c.data?.deceased_name || `${c.data?.husband_name || ""} & ${c.data?.wife_name || ""}`;
                  return (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs">{c.certificate_no}</td>
                      <td className="px-4 py-3 capitalize">{c.type}</td>
                      <td className="px-4 py-3 font-medium">{name}</td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button data-testid={`appr-cert-approve-${c.id}`} onClick={() => approve("certificates", c.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md text-white" style={{ background: "#107C10" }}>
                          <Check className="w-3 h-3" /> Approve
                        </button>
                        <button data-testid={`appr-cert-reject-${c.id}`} onClick={() => setRejecting({ kind: "certificates", id: c.id, label: c.certificate_no })}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md border border-red-200 text-red-700">
                          <X className="w-3 h-3" /> Reject
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {data.certificates.length === 0 && (
                  <tr><td colSpan={4} className="py-12 text-center text-slate-400 text-sm">No pending certificates</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {rejecting && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-md w-full max-w-md p-5">
              <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-red-600" /><h2 className="font-heading text-lg font-bold">Reject — {rejecting.label}</h2></div>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection"
                className="w-full px-3 py-2 rounded-md border border-slate-300 bg-white text-sm" data-testid="reject-reason" />
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => { setRejecting(null); setReason(""); }} className="px-4 py-2 text-sm rounded-md border border-slate-300">Cancel</button>
                <button onClick={reject} data-testid="confirm-reject" className="px-4 py-2 text-sm font-semibold rounded-md text-white bg-red-600">Reject</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 text-sm">
          <ShieldCheck className="w-5 h-5 text-amber-700 mt-0.5" />
          <div className="text-amber-900">
            <div className="font-semibold">Maker-Checker enforced</div>
            <div className="text-amber-800 mt-1">Maker and Checker must be different users. Required roles for Checker: Secretary, Sarpanch, Block, District, State.</div>
          </div>
        </div>
      </div>
    </OfficerLayout>
  );
}
